import React, { createContext, useContext, useState, useEffect } from 'react';
import { useError } from './ErrorContext';

const QRCodeContext = createContext();

// Initial values for QR code settings
const initialQRCodeValues = {
  contextQrCodeColor: '#000000', // Default black color
  contextQrCodeLogoUrl: '', // Default empty logo URL
};

export const QRCodeProvider = ({ children }) => {
  const [qrCodeState, setQRCodeState] = useState({});
  const [error, setError] = useState(null);

  // Get error context for global error handling
  const { addError } = useError();

  // Enhanced error handling for QR code operations
  const handleQRCodeError = (error, operation = 'qr-code-operation') => {
    console.error('QR code operation error:', error);

    // Only show user-relevant errors, log technical errors
    if (error.message?.includes('validation') ||
      error.message?.includes('must be') ||
      error.message?.includes('required') ||
      error.message?.includes('Invalid') ||
      error.message?.includes('permission') ||
      error.message?.includes('session')) {
      addError(error, {
        source: 'qr-code',
        type: operation,
        retryable: false,
        autoDismiss: true
      });
    }

    setError(error.message || 'An error occurred while updating QR code settings');
  };

  // Clear error after timeout
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Function to update QR code settings for a specific card with error handling
  const updateQRCodeData = (cardId, newData) => {
    try {
      if (!cardId) {
        throw new Error('Card ID is required to update QR code data');
      }

      setQRCodeState(prevState => {
        const updatedState = {
          ...prevState,
          [cardId]: {
            ...prevState[cardId],
            ...newData,
          },
        };
        console.log('Updated QR code settings:', updatedState[cardId]);
        return updatedState;
      });

      setError(null); // Clear any existing errors on successful update
    } catch (error) {
      handleQRCodeError(error, 'qr-code-update');
    }
  };

  // Function to initialize a QR code for a card with default values
  const initializeQRCode = (cardId) => {
    try {
      if (!cardId) {
        throw new Error('Card ID is required to initialize QR code');
      }

      if (!qrCodeState[cardId]) {
        setQRCodeState(prevState => ({
          ...prevState,
          [cardId]: initialQRCodeValues,
        }));
        console.log('QR code initialized for card:', cardId);
      }
    } catch (error) {
      handleQRCodeError(error, 'qr-code-initialization');
    }
  };

  // Function to clear error
  const clearError = () => {
    setError(null);
  };

  // Setter functions for QR code settings with error handling
  const setQRCodeColor = (cardId, color) => {
    try {
      if (typeof color !== 'string') {
        throw new Error('QR code color must be a string');
      }
      // Basic color validation (hex color format)
      if (color && !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
        throw new Error('Invalid color format. Please use hex color (e.g., #000000)');
      }
      updateQRCodeData(cardId, { contextQrCodeColor: color });
    } catch (error) {
      handleQRCodeError(error, 'qr-code-color-update');
    }
  };

  const setQRCodeLogoUrl = (cardId, url) => {
    try {
      if (typeof url !== 'string') {
        throw new Error('QR code logo URL must be a string');
      }
      // Enhanced URL validation to accept blob URLs, data URLs, and regular URLs
      if (url && !/^https?:\/\/.+/.test(url) && !/^data:image\/.+/.test(url) && !/^blob:/.test(url)) {
        throw new Error('Invalid URL format for QR code logo');
      }
      updateQRCodeData(cardId, { contextQrCodeLogoUrl: url });
    } catch (error) {
      handleQRCodeError(error, 'qr-code-logo-update');
    }
  };

  // Clear all QR code values to defaults for a specific card with error handling
  const clearQRCodeSettings = (cardId) => {
    try {
      if (!cardId) {
        throw new Error('Card ID is required to clear QR code settings');
      }

      setQRCodeState(prevState => ({
        ...prevState,
        [cardId]: initialQRCodeValues,
      }));
      console.log('QR Code settings cleared for card:', cardId);
    } catch (error) {
      handleQRCodeError(error, 'qr-code-clear');
    }
  };

  return (
    <QRCodeContext.Provider
      value={{
        qrCodeState,
        error,
        setQRCodeColor,
        setQRCodeLogoUrl,
        clearQRCodeSettings,
        initializeQRCode, // Expose the initialize function
        clearError, // Expose the clear error function
      }}
    >
      {children}
    </QRCodeContext.Provider>
  );
};

// Custom hook to use the QRCodeContext
export const useQRCodeContext = () => {
  const context = useContext(QRCodeContext);
  if (!context) {
    throw new Error('useQRCodeContext must be used within a QRCodeProvider');
  }
  return context;
};

export default QRCodeContext;
